import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { TokenRefreshService } from './token-refresh.service';
import { TokenService } from './token.service';
import { isStaticAssetRequest } from '../utilities/http-request.utils';

// Must match UserSecurity.ReissuedTokenHeader on the backend.
const REISSUED_TOKEN_HEADER = 'X-Reissued-Token';

// Must match TokenRefreshService; a 401 from the refresh call must not trigger another refresh.
const REFRESH_ENDPOINT = 'Authenticate/refresh';

/**
 * Attaches the JWT (if we have one) as a Bearer Authorization header on every outgoing request -
 * this is what replaces the browser silently sending the auth cookie - and swaps in a replacement
 * token whenever the server sends one back.
 *
 * The cookie flow got a "switch organization" update for free: the server re-signed the cookie in
 * place and the browser carried the new one from then on. A bearer token can't be rewritten after
 * it's issued, so the server mints a whole new one (UserSecurity.ReissueCredentialAsync) any time
 * it changes our claims - selected organization, tank profile, impersonation - and returns it on
 * the header above. Handling that here rather than at each call site is deliberate: an endpoint
 * opts in purely by changing claims server-side, so there is no per-endpoint client wiring anyone
 * can forget, which is exactly how the selected organization silently drifted out of sync with the
 * data the app was showing.
 */
@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  // TokenRefreshService depends on HttpClient, so injecting it directly would be a cycle while the
  // interceptor chain is still being built. Resolved lazily, once a 401 has actually happened -
  // same reason and same shape as AuthInterceptor's Injector use.
  constructor(
    private tokenService: TokenService,
    private injector: Injector,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Static assets (e.g. the SVGs angular-svg-icon fetches) aren't API calls and don't need -
    // or want - the bearer token attached.
    if (isStaticAssetRequest(request.url)) {
      return next.handle(request);
    }

    return next.handle(this.withBearer(request)).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          // Header lookups here are case-insensitive, so the wire casing doesn't matter.
          const reissuedToken = event.headers.get(REISSUED_TOKEN_HEADER);
          if (reissuedToken) {
            this.tokenService.setTokenFromJwt(reissuedToken);
          }
        }
      }),
      catchError((err: unknown) => {
        if (!this.shouldTryRefresh(err, request)) return throwError(() => err);

        return this.injector
          .get(TokenRefreshService)
          .refresh()
          .pipe(
            switchMap((refreshed) =>
              refreshed
                ? next.handle(this.withBearer(request))
                : throwError(() => err),
            ),
          );
      }),
    );
  }

  private withBearer(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = this.tokenService.getAccessToken();
    return token
      ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : request;
  }

  /**
   * Gated on our own token being absent or expired, not on the 401 alone, and that distinction is
   * the point.
   *
   * A 401 in this API does not mean "logged out". Around 25 controller actions answer a failed
   * HasOrganizationAccess with Unauthorized(), and Startup maps UnauthorizedAccessException to 401
   * too - those mean "not your row" (see the comment on EXPIRE_SESSION_ON_401). Refreshing on every
   * 401 would spend a round trip and re-send the request for each of them, doubling the cost of
   * every scope refusal in the app to fix a problem it does not have. The token's own expiry is
   * readable client-side, so the one case where a refresh can possibly help is checkable up front.
   *
   * The refresh call itself is excluded, or its own 401 would ask it to refresh again.
   */
  private shouldTryRefresh(err: unknown, request: HttpRequest<unknown>): boolean {
    return (
      err instanceof HttpErrorResponse &&
      err.status === 401 &&
      !request.url.includes(REFRESH_ENDPOINT) &&
      this.tokenService.isExpired()
    );
  }
}

export const jwtInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: JwtInterceptor,
  multi: true,
};
